import React, {useContext, useState, useMemo, useCallback} from "react";
import { useDispatch } from "react-redux";

import PopupWin from "../../../components/PopupWin";
import PopupButton from "../../../components/PopupButton";
import Button from "../../../components/Button";
import Toaster from "../../../components/Toaster/index.js";
import ContextProvider from "../../../utils/ContextApi.js";
import {columnsWidths, returnParsedColumnName} from "../../../utils/columns.js";
import {calculateAllUniqueValues, applyFilters, initializeFilters} from "../../../utils/tableUtils";
import {sortData, handleSortChange} from "../../../utils/sortUtils";
import * as campaignActions from "../../../redux/Campaign/actions";
import './AccountManagerReportPopup.scss';
import { useNavigate } from "react-router-dom";
import FilterIcon from "../../../components/FilterIcon";
import filterIcon from '../../../assets/components/filters/filter.svg';
import useColumnFilters from '../../../hooks/useColumnFilters';
import useDisplayedColumns from "../../../hooks/dispalyCoulmns.js";
const AccountManagerReportPopup = () => {
	const { accountManagerReportData, setAccountManagerReportData, setHeader, header } = useContext(ContextProvider);	
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// Memoize data transformation
	const data = useMemo(() => {
		if (!accountManagerReportData) return [];
		return accountManagerReportData.map(({ original_name, contact_id, accounts_type_id,...rest }) => rest);
	}, [accountManagerReportData]);

	const metadata = header?.metadata;
	const [addToCampaignAccounts, setAddToCampaignAccounts] = useState(metadata?.relatedAccounts || []);
	const [filters, setFilters] = useState({});
	const [sortBy, setSortBy] = useState(null);
	const [sortOrder, setSortOrder] = useState(null);
	const HIDDEN_FIELDS = [];
	// const HIDDEN_FIELDS = ['original_name', 'contact_id', 'accounts_type_id', 'account_id'];
	const onClose = useCallback(() => {
		setAccountManagerReportData(null);
		setHeader(null);
	}, [setAccountManagerReportData, setHeader]);

	const getColumnWidth = useCallback(caption => {
		return columnsWidths[caption] || '130px';
	}, []);

	const updateFilter = useCallback((value, column) => {
		setFilters(prev => ({
			...prev,
			[column]: value
		}));
	}, []);

const handleAddToCampaignAccounts = useCallback((account) => {
	setAddToCampaignAccounts(prev => {
		const alreadySelected = prev.some(a => a.account_id === account.account_id);
		const updated = alreadySelected
			? prev.filter(a => a.account_id !== account.account_id)
			: [...prev, account]; // Make sure account includes account_name too

		console.log('Updated addToCampaignAccounts:', updated);
		return updated;
	});
}, []);

	const isAccountSelected = useCallback((account) => {
		return addToCampaignAccounts.some(a => a.account_id === account.account_id);
	}, [addToCampaignAccounts]);

	const addToCampaignButtonDisabled = useCallback(() => {
		const noAccountsSelected = addToCampaignAccounts.length === 0;
		const initialAccounts = metadata?.relatedAccounts || [];
		const sameAsInitial = initialAccounts.length === addToCampaignAccounts.length && 
			initialAccounts.every(initialAccount => 
				addToCampaignAccounts.some(selectedAccount => 
					selectedAccount.account_id === initialAccount.account_id
				)
			);
		return noAccountsSelected || sameAsInitial;
	}, [addToCampaignAccounts, metadata?.relatedAccounts]);

	const isCheckboxDisabled = useCallback((account) => {
		return addToCampaignAccounts.length > 2 && !isAccountSelected(account);
	}, [addToCampaignAccounts, isAccountSelected]);

	const addCampaign = useCallback(() => {	
		dispatch(campaignActions.addRelativeAccountsToContact(
			metadata.campaignId, 
			metadata.contactId, 
			addToCampaignAccounts, 
			(result) => {
				if(result.status === "ok") {
					Toaster.showSuccess("Added to campaign!");
					navigate(`/campaign/${metadata.campaignId}`);
				} else {
					Toaster.showError(result.message);
				}
			}
		));
		onClose();
	}, [dispatch, metadata, addToCampaignAccounts, navigate, onClose]);

	const sortDataWithCheckedFirst = useCallback((data) => {
		if (!metadata || metadata.source !== 'Campaign members') return data;
		
		return [...data].sort((a, b) => {
			const isASelected = isAccountSelected(a);
			const isBSelected = isAccountSelected(b);
			
			if (isASelected && !isBSelected) return -1;
			if (!isASelected && isBSelected) return 1;
			return 0;
		});
	}, [metadata, isAccountSelected]);
	
	const headers = useMemo(() => data && data[0] ? Object.keys(data[0]) : [], [data]);
		const {
		displayedColumns,
		setDisplayedColumns,
		columnsFilterDisplay,
		setColumnsFilterDisplay,
		changeColumnFilterDisplay,
		isColumnDisplay,
		closeColumnsFilter,
		availebleColumns
	} = useDisplayedColumns({
		headers,
		storageKey: `accountManagerReport_${header.tableName || 'default'}`
	});

	// Memoized footer for main popup
	const mainFooter = useMemo(() => {
		if (metadata && metadata.source === 'Campaign members') {
			return (
				<>
					<PopupButton variant="secondary" onClick={onClose}>Cancel</PopupButton>
					<PopupButton
						variant="primary"
						disabled={addToCampaignButtonDisabled()}
						onClick={addCampaign}>
						Add to campaign
					</PopupButton>
				</>
			);
		}
		return (
			<>
				<PopupButton variant="secondary" onClick={onClose}>Cancel</PopupButton>
			</>
		);
	}, [metadata, onClose, addToCampaignButtonDisabled, addCampaign]);

	// Memoized footer for columns filter popup
	const columnsFilterFooter = useMemo(() => (
		<>
			<PopupButton variant="secondary" onClick={closeColumnsFilter}>Cancel</PopupButton>
		</>
	), [closeColumnsFilter]);
	const columns = useMemo(() => displayedColumns.map(key => ({ id: key })), [displayedColumns]);	
	
	const {
		filters: columnFilters,
		sortBy: hookSortBy,
		sortOrder: hookSortOrder,
		filteredData,
		handleFilterChange,
		filterOrder,
		resetWith,
		updateData,
		uniqueValuesByColumn
	} = useColumnFilters({
		data,
		columns,
		initialState: {},
		storageKey: null
	});

	// Apply text input filters
	const filteredDataWithText = useMemo(() => {
		if (!filteredData) return [];
		
		return filteredData.filter(row => {
			return Object.entries(filters).every(([key, value]) => {
				if (!value) return true;
				const cellValue = row[key];
				if (cellValue === undefined || cellValue === null) return false;
				return cellValue.toString().toLowerCase().includes(value.toString().toLowerCase());
			});
		});
	}, [filteredData, filters]);
	
	const sortedData = useMemo(() => {
		let result = sortData(filteredDataWithText, sortBy, sortOrder);
		return sortDataWithCheckedFirst(result);
	}, [filteredDataWithText, sortBy, sortOrder, sortDataWithCheckedFirst]);

	if(!data || !data[0]){
		return <div />
	}

	return (
		<PopupWin
			closeOnEscape={false}
			closeOnDocumentClick={false}
			visible={true}
			onClose={onClose}
			title={header.name}
			subtitle={
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<span>{header.value} ({data.length} {header.tableName})</span>
				<Button
				type="blue"
				width={210}
				leftIcon={filterIcon}
				onClick={() => setColumnsFilterDisplay(true)}
				>
				Select displayed columns
				</Button>
			</div>
			}
			contentStyle={{width: '100%', height: '90%'}}
			popupStyle={{width: '90%', maxWidth: '1600px', height: '80%', maxHeight: '800px', overflow: 'none'}}
			footer={mainFooter}>

			<table className="mapping-table" style={{userSelect: 'none'}}>
				<thead>
				<tr>
					{displayedColumns.map((key) => (
						<th key={key} title={key} style={{width: getColumnWidth(key), whiteSpace: 'nowrap'}}>
							<div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
								{returnParsedColumnName(key)}
								<FilterIcon
									column={key}
									data={data}
									allUniqueValues={uniqueValuesByColumn[key]}
									onFilterChange={handleFilterChange}
									filters={columnFilters}
									sortBy={hookSortBy}
									sortOrder={hookSortOrder}
									filterOrderIndex={filterOrder?.indexOf(key)}
								/>
							</div>
						</th>
					))}
				</tr>
				</thead>
				<tbody>
				<tr>
					{displayedColumns.map((key, i) => (
						<td key={i} style={{width: getColumnWidth(key)}}>
							<input type="text" value={filters[key] || ""} onChange={e => updateFilter(e.target.value, key)} style={{width: getColumnWidth(key), outline: 'none', padding: '5px', border: '1px solid #ccc', borderRadius: '4px'}} />
						</td>
					))}
				</tr>
				{
					sortedData && sortedData.map((row, index) => {						
						return (
							<tr key={row.account_id || index}>
								{
									displayedColumns.map((key) => {
										return (
											<td key={key} title={key} style={{width: getColumnWidth(key), color: 'black'}}>
												{metadata && metadata.source === 'Campaign members' && key === 'account_name' && <input type="checkbox" checked={isAccountSelected(row)} onChange={() => handleAddToCampaignAccounts(row)} disabled={isCheckboxDisabled(row)}/>}
												{ row[key] }
											</td>
										)
									})
								}
							</tr>
						)
					})
				}
				</tbody>
			</table>
			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={columnsFilterDisplay}
				onClose={closeColumnsFilter}
				title="Select the columns you want to display"
				footer={columnsFilterFooter}>
				<div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
					{availebleColumns
					.map(header => (
						<div key={header} style={{ display: 'flex', alignItems: 'center' }}>
						<input
							type="checkbox"
							checked={isColumnDisplay(header)}
							onChange={e => changeColumnFilterDisplay(e.target.checked, header)}
						/>
						<div>{returnParsedColumnName(header)}</div>
						</div>
					))}
				</div>
			</PopupWin>

		</PopupWin>
	)
};

export default AccountManagerReportPopup;
